import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Message from "./Message";
import { Button } from "../../ui/button";
import { Skeleton } from "../../ui/skeleton";
import { cn } from "../../../lib/utils";
import {
    formatTimeSeparatorLabel,
    shouldShowTimeSeparator,
} from "./messageTimeUtils";
import { buildReadersByMessageId } from "./readIndicatorUtils";

/** Start loading older messages when the user is this close to the top. */
const LOAD_MORE_TOP_THRESHOLD_PX = 96;

/** Treat the viewport as following the latest messages within this distance. */
const STICK_TO_BOTTOM_PX = 80;

/** Scrollbar / scrollTop ease duration after a prepend. */
const SCROLLBAR_TRANSITION_MS = 500;

/** Brief pause before another top-edge load can fire. */
const LOAD_MORE_COOLDOWN_MS = 320;

/** How long a jumped-to parent stays highlighted before the accent ring fades. */
const PARENT_HIGHLIGHT_MS = 1600;

/** Duration to ignore scroll events during a smooth pin-to-bottom. */
const SMOOTH_PIN_MS = 400;

/**
 * Whether two messages belong to the same consecutive sender cluster.
 * @param {Object|null|undefined} a
 * @param {Object|null|undefined} b
 * @returns {boolean}
 */
function isSameSenderCluster(a, b) {
    if (!a || !b) return false;
    if (a.message_type === "system" || b.message_type === "system") return false;
    if (a.sender_id == null || b.sender_id == null) return false;
    return Number(a.sender_id) === Number(b.sender_id);
}

/**
 * Escape a value for use in a CSS attribute selector.
 * @param {string} value
 * @returns {string}
 */
function escapeSelectorValue(value) {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
        return CSS.escape(value);
    }
    return String(value).replace(/["\\]/g, "\\$&");
}

/**
 * Ease-out cubic.
 * @param {number} t
 * @returns {number}
 */
function easeOutCubic(t) {
    return 1 - (1 - t) ** 3;
}

/**
 * Whether the user prefers reduced motion.
 * @returns {boolean}
 */
function prefersReducedMotion() {
    return (
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
}

/**
 * Maximum scrollTop for a container.
 * @param {HTMLElement} container
 * @returns {number}
 */
function maxScrollTop(container) {
    return Math.max(0, container.scrollHeight - container.clientHeight);
}

/**
 * Whether the container is scrolled to (or very near) the bottom.
 * @param {HTMLElement} container
 * @returns {boolean}
 */
function isNearBottom(container) {
    return maxScrollTop(container) - container.scrollTop <= STICK_TO_BOTTOM_PX;
}

/**
 * MessageList component - Scrollable container for messages
 * @param {Object} props
 * @param {Object} [props.chat]
 * @param {number} [props.chatId]
 * @param {Array} props.messages - Array of message objects
 * @param {boolean} props.loading - Initial loading state
 * @param {boolean} [props.loadingMore] - Older-page loading state
 * @param {string} props.error - Error message
 * @param {number} props.userId - Current user ID
 * @param {function} props.onLoadMore - Callback to load older messages
 * @param {boolean} props.hasMore - Whether older messages exist
 * @param {function} [props.onRetry] - Retry initial load
 * @param {(messageId: number) => void} [props.onMessageDeleted]
 * @param {(reactionId: number) => void|Promise<void>} [props.onRemoveOwnReaction]
 * @param {(message: Object) => void} [props.onReply]
 * @param {number} [props.focusMessageId]
 * @param {number} [props.focusKey]
 */
function MessageList({
    chat,
    chatId,
    messages,
    loading,
    loadingMore = false,
    error,
    userId,
    onLoadMore,
    hasMore,
    onRetry,
    onMessageDeleted,
    onRemoveOwnReaction,
    onReply,
    focusMessageId = null,
    focusKey = 0,
}) {
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    const didInitialLoadRef = useRef(false);
    const didInitialPinRef = useRef(false);
    const stickToBottomRef = useRef(true);
    const programmaticScrollRef = useRef(false);
    const programmaticScrollClearRef = useRef(null);
    const previousKeysRef = useRef(new Set());
    const skipIncomingAnimationRef = useRef(false);
    const prependAnchorRef = useRef(null);
    const isPrependingRef = useRef(false);
    const prevNewestIdRef = useRef(null);
    const loadCooldownUntilRef = useRef(0);
    const scrollAnimRef = useRef(null);
    const highlightTimerRef = useRef(null);
    const scrolledFocusRef = useRef(null);
    const holdingFocusRef = useRef(false);
    const focusedMessageIdRef = useRef(null);
    const [animatedMessageKeys, setAnimatedMessageKeys] = useState(new Set());
    const [highlightedMessageId, setHighlightedMessageId] = useState(null);

    const readersByMessageId = useMemo(
        () =>
            buildReadersByMessageId(
                messages,
                chat?.other_members,
                userId
            ),
        [messages, chat?.other_members, userId]
    );

    const getMessageKey = (message, index) =>
        String(
            message.local_key ??
                message.id ??
                `${message.created_at || message.timestamp || "msg"}-${index}`
        );

    /**
     * Ignore onScroll for a short window after we set scrollTop ourselves.
     * @param {number} [durationMs]
     * @returns {void}
     */
    function beginProgrammaticScroll(durationMs = 0) {
        programmaticScrollRef.current = true;
        if (programmaticScrollClearRef.current != null) {
            cancelAnimationFrame(programmaticScrollClearRef.current);
            clearTimeout(programmaticScrollClearRef.current);
            programmaticScrollClearRef.current = null;
        }
        if (durationMs > 0) {
            programmaticScrollClearRef.current = setTimeout(() => {
                programmaticScrollRef.current = false;
                programmaticScrollClearRef.current = null;
            }, durationMs);
            return;
        }
        programmaticScrollClearRef.current = requestAnimationFrame(() => {
            programmaticScrollClearRef.current = requestAnimationFrame(() => {
                programmaticScrollRef.current = false;
                programmaticScrollClearRef.current = null;
            });
        });
    }

    /**
     * Pin the message pane to the latest messages.
     * @param {"auto"|"smooth"} [behavior]
     * @returns {void}
     */
    function pinToBottom(behavior = "auto") {
        const container = containerRef.current;
        if (!container) return;

        stickToBottomRef.current = true;
        const top = maxScrollTop(container);
        if (behavior === "smooth" && !prefersReducedMotion()) {
            beginProgrammaticScroll(SMOOTH_PIN_MS);
            container.scrollTo({ top, behavior: "smooth" });
            return;
        }
        beginProgrammaticScroll();
        container.scrollTop = top;
    }

    /**
     * Scroll only the message pane so a target row is centered.
     * Avoids `scrollIntoView`, which can move ancestor page scrollers.
     * @param {number} messageId
     * @param {"auto"|"smooth"} [behavior]
     * @param {{ highlight?: boolean }} [options]
     * @returns {boolean}
     */
    function scrollToMessageId(messageId, behavior = "auto", options = {}) {
        const id = Number(messageId);
        if (!Number.isFinite(id) || id <= 0) return false;
        const container = containerRef.current;
        if (!container) return false;
        const selector = `[data-message-anchor="${escapeSelectorValue(String(id))}"]`;
        const el = container.querySelector(selector);
        if (!el) return false;

        holdingFocusRef.current = true;
        focusedMessageIdRef.current = id;
        stickToBottomRef.current = false;

        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const delta =
            elRect.top -
            containerRect.top -
            container.clientHeight / 2 +
            elRect.height / 2;
        if (Math.abs(delta) < 1) {
            return true;
        }
        const top = Math.max(
            0,
            Math.min(container.scrollTop + delta, maxScrollTop(container))
        );

        if (behavior === "smooth" && !prefersReducedMotion()) {
            beginProgrammaticScroll(SMOOTH_PIN_MS);
            container.scrollTo({ top, behavior: "smooth" });
        } else {
            beginProgrammaticScroll();
            container.scrollTop = top;
        }

        if (options.highlight === false) {
            return true;
        }

        setHighlightedMessageId(id);
        if (highlightTimerRef.current) {
            clearTimeout(highlightTimerRef.current);
        }
        highlightTimerRef.current = setTimeout(() => {
            setHighlightedMessageId(null);
            highlightTimerRef.current = null;
        }, PARENT_HIGHLIGHT_MS);
        return true;
    }

    /**
     * Scroll to a parent message if it is currently loaded, then briefly highlight it.
     * @param {number} parentId
     * @returns {void}
     */
    function scrollToParentMessage(parentId) {
        scrollToMessageId(parentId, prefersReducedMotion() ? "auto" : "smooth");
    }

    useLayoutEffect(() => {
        const id = Number(focusMessageId);
        if (!Number.isFinite(id) || id <= 0 || loading) return;
        const token = `${id}:${focusKey}`;
        if (scrolledFocusRef.current === token) return;
        if (!messages.some((m) => Number(m.id) === id)) return;
        scrolledFocusRef.current = token;
        scrollToMessageId(id, "auto");
    }, [focusMessageId, focusKey, loading, messages]);

    /**
     * Cancel an in-flight scrollbar transition and clear content transform.
     * @param {{ commit?: boolean }} [options] - When commit is true, leave scrollTop as-is.
     * @returns {void}
     */
    function cancelScrollTransition(options = {}) {
        const anim = scrollAnimRef.current;
        if (anim?.rafId != null) {
            cancelAnimationFrame(anim.rafId);
        }
        if (anim && !options.commit && containerRef.current && Number.isFinite(anim.endScroll)) {
            containerRef.current.scrollTop = anim.endScroll;
        }
        if (contentRef.current) {
            contentRef.current.style.transform = "";
            contentRef.current.style.willChange = "";
        }
        scrollAnimRef.current = null;
    }

    /**
     * Capture the first visible message row (viewport-relative).
     * @returns {{ key: string, viewportTop: number } | null}
     */
    function captureScrollAnchor() {
        const container = containerRef.current;
        if (!container) return null;

        const containerTop = container.getBoundingClientRect().top;
        const rows = container.querySelectorAll("[data-message-anchor]");
        for (const row of rows) {
            const rect = row.getBoundingClientRect();
            if (rect.bottom > containerTop + 1) {
                return {
                    key: row.getAttribute("data-message-anchor") || "",
                    viewportTop: rect.top,
                };
            }
        }
        return null;
    }

    /**
     * How far the anchored row moved in the viewport after a prepend (px down).
     * @param {{ key: string, viewportTop: number }} anchor
     * @returns {number|null}
     */
    function measureAnchorDelta(anchor) {
        const container = containerRef.current;
        if (!container || !anchor?.key) return null;

        const row = container.querySelector(
            `[data-message-anchor="${escapeSelectorValue(anchor.key)}"]`
        );
        if (!row) return null;

        return row.getBoundingClientRect().top - anchor.viewportTop;
    }

/** Fraction of the viewport to reveal above during the pagination transition. */
const PAGINATION_PEEK_RATIO = 0.25;

/**
 * Keep the same messages on screen while easing the scrollbar over ~500ms,
 * drifting upward ~10% of the viewport so newly loaded history is peeking in.
 * scrollTop and an inverse translateY move together so content does not jump.
 * @param {number} delta - Extra scroll needed after content was prepended.
 * @returns {void}
 */
function runPinnedScrollbarTransition(delta) {
        const container = containerRef.current;
        const content = contentRef.current;
        if (!container || !content || !Number.isFinite(delta) || Math.abs(delta) < 0.5) {
            return;
        }

        cancelScrollTransition({ commit: true });

        const startScroll = container.scrollTop;
        const peek = Math.min(
            container.clientHeight * PAGINATION_PEEK_RATIO,
            Math.max(0, startScroll + delta)
        );
        const endScroll = Math.max(0, startScroll + delta - peek);

        if (prefersReducedMotion()) {
            container.scrollTop = endScroll;
            content.style.transform = "";
            content.style.willChange = "";
            return;
        }

        // Before paint: pin content with transform; leave scrollTop so the thumb
        // still sits at the pre-prepend position, then ease both over 500ms.
        content.style.willChange = "transform";
        content.style.transform = `translate3d(0, ${-delta}px, 0)`;

        const startedAt = performance.now();
        const anim = { rafId: 0, endScroll };
        scrollAnimRef.current = anim;

        /**
         * @param {number} now
         * @returns {void}
         */
        function frame(now) {
            if (scrollAnimRef.current !== anim) return;

            const t = Math.min(1, (now - startedAt) / SCROLLBAR_TRANSITION_MS);
            const p = easeOutCubic(t);
            // Lock compensation: delta*p scroll vs -delta*(1-p) transform.
            // Extra -peek*p eases the view upward to reveal older messages.
            container.scrollTop = startScroll + (delta - peek) * p;
            content.style.transform = `translate3d(0, ${-delta * (1 - p)}px, 0)`;

            if (t < 1) {
                anim.rafId = requestAnimationFrame(frame);
                return;
            }

            container.scrollTop = endScroll;
            content.style.transform = "";
            content.style.willChange = "";
            scrollAnimRef.current = null;
            loadCooldownUntilRef.current = Date.now() + LOAD_MORE_COOLDOWN_MS;
        }

        anim.rafId = requestAnimationFrame(frame);
    }

    /**
     * Request the next older page while remembering the current viewport anchor.
     * @returns {void}
     */
    function requestOlderMessages() {
        if (!containerRef.current || !hasMore || loading || loadingMore) return;
        if (isPrependingRef.current || prependAnchorRef.current) return;
        if (scrollAnimRef.current) return;
        if (Date.now() < loadCooldownUntilRef.current) return;

        prependAnchorRef.current = captureScrollAnchor();
        isPrependingRef.current = true;
        skipIncomingAnimationRef.current = true;
        onLoadMore();
    }

    /**
     * While following the bottom, fetch older pages if the list cannot be
     * scrolled up (same loading chip; pin stays at the latest messages).
     * @returns {void}
     */
    function maybeBackfillOlder() {
        const container = containerRef.current;
        if (!container || !didInitialPinRef.current) return;
        if (!stickToBottomRef.current) return;
        if (container.scrollTop > LOAD_MORE_TOP_THRESHOLD_PX) return;
        requestOlderMessages();
    }

    const handleScroll = () => {
        if (scrollAnimRef.current) return;
        if (programmaticScrollRef.current) return;
        const container = containerRef.current;
        if (!container) return;

        stickToBottomRef.current = isNearBottom(container);
        holdingFocusRef.current = false;
        if (stickToBottomRef.current) {
            focusedMessageIdRef.current = null;
        }

        if (!didInitialPinRef.current) return;
        if (stickToBottomRef.current) return;
        if (!hasMore || loading || loadingMore) return;
        if (isPrependingRef.current) return;
        if (Date.now() < loadCooldownUntilRef.current) return;
        if (container.scrollTop <= LOAD_MORE_TOP_THRESHOLD_PX) {
            requestOlderMessages();
        }
    };

    useEffect(() => {
        didInitialLoadRef.current = false;
        didInitialPinRef.current = false;
        stickToBottomRef.current = true;
        programmaticScrollRef.current = false;
        if (programmaticScrollClearRef.current != null) {
            cancelAnimationFrame(programmaticScrollClearRef.current);
            clearTimeout(programmaticScrollClearRef.current);
            programmaticScrollClearRef.current = null;
        }
        previousKeysRef.current = new Set();
        prevNewestIdRef.current = null;
        skipIncomingAnimationRef.current = false;
        prependAnchorRef.current = null;
        isPrependingRef.current = false;
        loadCooldownUntilRef.current = 0;
        holdingFocusRef.current = false;
        focusedMessageIdRef.current = null;
        scrolledFocusRef.current = null;
        cancelScrollTransition({ commit: true });
        setAnimatedMessageKeys(new Set());
        setHighlightedMessageId(null);
        if (highlightTimerRef.current) {
            clearTimeout(highlightTimerRef.current);
            highlightTimerRef.current = null;
        }
    }, [chat?.id]);

    useEffect(
        () => () => {
            cancelScrollTransition({ commit: true });
            if (highlightTimerRef.current) {
                clearTimeout(highlightTimerRef.current);
            }
            if (programmaticScrollClearRef.current != null) {
                cancelAnimationFrame(programmaticScrollClearRef.current);
                clearTimeout(programmaticScrollClearRef.current);
            }
        },
        []
    );

    // After older messages mount: pin the viewport, ease only the scrollbar.
    // Initial / newest-message: pin to bottom before paint so the list does not flash at the top.
    useLayoutEffect(() => {
        if (messages.length === 0) return;

        const newestId = messages[messages.length - 1]?.id ?? null;
        const isInitial = prevNewestIdRef.current == null;
        const newestChanged =
            isInitial || Number(newestId) !== Number(prevNewestIdRef.current);
        prevNewestIdRef.current = newestId;

        if (prependAnchorRef.current) {
            // Overlay loader does not change layout; wait for the page to arrive.
            if (loadingMore) return;

            const anchor = prependAnchorRef.current;
            prependAnchorRef.current = null;
            isPrependingRef.current = false;

            if (stickToBottomRef.current) {
                pinToBottom("auto");
                didInitialPinRef.current = true;
                return;
            }

            const delta = measureAnchorDelta(anchor);
            if (delta == null) {
                loadCooldownUntilRef.current = Date.now() + LOAD_MORE_COOLDOWN_MS;
                return;
            }

            runPinnedScrollbarTransition(delta);
            return;
        }

        if (isPrependingRef.current || scrollAnimRef.current) return;
        if (!newestChanged) return;

        const pendingFocusId =
            focusMessageId != null ? Number(focusMessageId) : null;
        if (isInitial && Number.isFinite(pendingFocusId) && pendingFocusId > 0) {
            stickToBottomRef.current = false;
            holdingFocusRef.current = true;
            focusedMessageIdRef.current = pendingFocusId;
            didInitialPinRef.current = true;
            return;
        }

        if (!isInitial && !stickToBottomRef.current) {
            const newest = messages[messages.length - 1];
            const isOwnNew =
                newest != null && Number(newest.sender_id) === Number(userId);
            if (!isOwnNew) return;
            holdingFocusRef.current = false;
            focusedMessageIdRef.current = null;
        }

        pinToBottom(isInitial ? "auto" : "smooth");
        didInitialPinRef.current = true;
    }, [messages, loadingMore, focusMessageId, userId]);

    useEffect(() => {
        if (!didInitialPinRef.current) return;
        maybeBackfillOlder();
    }, [messages, loadingMore, hasMore, loading]);

    // Keep the latest messages in view when images / receipts / the pane resize.
    const listMounted = messages.length > 0 && !error;
    useLayoutEffect(() => {
        if (!listMounted) return undefined;
        const container = containerRef.current;
        const content = contentRef.current;
        if (!container || !content) return undefined;

        const observer = new ResizeObserver(() => {
            if (isPrependingRef.current || prependAnchorRef.current) return;
            if (scrollAnimRef.current) return;
            if (stickToBottomRef.current) {
                pinToBottom("auto");
                return;
            }
            if (holdingFocusRef.current && focusedMessageIdRef.current) {
                scrollToMessageId(focusedMessageIdRef.current, "auto", {
                    highlight: false,
                });
            }
        });
        observer.observe(content);
        observer.observe(container);
        return () => observer.disconnect();
    }, [listMounted]);

    useEffect(() => {
        const currentKeys = new Set(
            messages.map((message, index) => getMessageKey(message, index))
        );

        if (!didInitialLoadRef.current) {
            didInitialLoadRef.current = true;
            previousKeysRef.current = currentKeys;
            return;
        }

        if (skipIncomingAnimationRef.current) {
            skipIncomingAnimationRef.current = false;
            previousKeysRef.current = currentKeys;
            setAnimatedMessageKeys(new Set());
            return;
        }

        const incomingKeys = new Set();
        currentKeys.forEach((key) => {
            if (!previousKeysRef.current.has(key)) {
                incomingKeys.add(key);
            }
        });

        setAnimatedMessageKeys(incomingKeys);
        previousKeysRef.current = currentKeys;
    }, [messages]);

    useEffect(() => {
        if (!loadingMore && prependAnchorRef.current == null) {
            skipIncomingAnimationRef.current = false;
            isPrependingRef.current = false;
        }
    }, [loadingMore]);

    if (error) {
        return (
            <div className="flex-1 flex items-center justify-center bg-primary">
                <div className="text-center text-text-muted">
                    <div className="text-error mb-2">⚠️</div>
                    <div className="font-medium mb-1">Failed to load messages</div>
                    <div className="text-sm">{error}</div>
                    <Button onClick={() => (onRetry ? onRetry() : onLoadMore())} variant="outline" className="mt-3">
                        Try Again
                    </Button>
                </div>
            </div>
        );
    }

    if (messages.length === 0 && loading) {
        return (
            <div className="flex-1 flex items-center justify-center bg-primary">
                <div className="text-center">
                    <Skeleton className="mx-auto h-6 w-6 rounded-full" />
                    <div className="text-sm text-text-muted mt-2">Loading messages...</div>
                </div>
            </div>
        );
    }

    if (messages.length === 0 && !loading) {
        return (
            <div className="flex-1 flex items-center justify-center bg-primary">
                <div className="text-center text-text-muted">
                    <div className="text-4xl mb-4">💬</div>
                    <div className="font-medium mb-1">No messages yet</div>
                    <div className="text-sm">Start the conversation by sending a message</div>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto bg-primary px-2 py-4 list-scrollbar lg:px-3"
            style={{ overflowAnchor: "none" }}
            onScroll={handleScroll}
        >
            <div ref={contentRef} className="relative">
                {/* Zero-height overlay — loading chip does not shift message layout. */}
                <div className="sticky top-0 z-10 h-0 overflow-visible">
                    <div
                        className={cn(
                            "flex justify-center pt-1 transition-opacity duration-200 ease-out",
                            loadingMore ? "opacity-100" : "pointer-events-none opacity-0"
                        )}
                        aria-hidden={!loadingMore}
                    >
                        <div className="inline-flex items-center gap-2 rounded-full border border-ui-border bg-ui-card/95 px-3 py-1.5 shadow-sm">
                            <Skeleton className="h-3.5 w-3.5 rounded-full" />
                            <span className="text-xs text-text-muted">Loading earlier messages…</span>
                        </div>
                    </div>
                </div>

                {messages.map((message, index) => {
                    const messageKey = getMessageKey(message, index);
                    const prev = messages[index - 1];
                    const next = messages[index + 1];
                    const showTimeSeparator = shouldShowTimeSeparator(prev, message);
                    const nextShowsTimeSeparator = shouldShowTimeSeparator(
                        message,
                        next
                    );
                    // Time gaps break sender clusters so avatar/name reset after a stamp.
                    const groupedWithPrev =
                        isSameSenderCluster(prev, message) && !showTimeSeparator;
                    const groupedWithNext =
                        isSameSenderCluster(message, next) && !nextShowsTimeSeparator;
                    const showHeader = !groupedWithPrev;
                    const showAvatar = !groupedWithNext;
                    const hasReactions =
                        !message.deleted_at &&
                        Array.isArray(message.reactions) &&
                        message.reactions.length > 0;
                    const readers =
                        message.id != null
                            ? readersByMessageId.get(Number(message.id)) || []
                            : [];
                    const hasReaders = readers.length > 0;
                    const separatorLabel = showTimeSeparator
                        ? formatTimeSeparatorLabel(
                              message.created_at || message.timestamp
                          )
                        : "";

                    return (
                        <div
                            key={messageKey}
                            className={cn(
                                groupedWithNext
                                    ? hasReactions || hasReaders
                                        ? "mb-3"
                                        : "mb-1"
                                    : "mb-4"
                            )}
                            style={{ overflowAnchor: "none" }}
                        >
                            {showTimeSeparator && separatorLabel ? (
                                <div
                                    className="flex w-full justify-center px-4 py-2"
                                    aria-hidden
                                >
                                    <p className="max-w-md text-center text-xs text-text-muted">
                                        {separatorLabel}
                                    </p>
                                </div>
                            ) : null}
                            <Message
                                chatType={chat?.chat_type}
                                message={message}
                                isOwnMessage={message.sender_id === userId}
                                userId={userId}
                                chatId={chatId || chat?.id}
                                animateOnMount={animatedMessageKeys.has(messageKey)}
                                showHeader={showHeader}
                                showAvatar={showAvatar}
                                scrollAnchorId={messageKey}
                                onMessageDeleted={onMessageDeleted}
                                onRemoveOwnReaction={onRemoveOwnReaction}
                                onReply={onReply}
                                onJumpToParent={scrollToParentMessage}
                                highlighted={Number(message.id) === highlightedMessageId}
                                readers={readers}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default MessageList;
